import { useCallback, useEffect, useState } from 'react'

import {
  FETCH_FINANCIAL_ACCOUNT_KEY,
  fetchFinancialAccountsIfNeeded,
} from '@/actions/financialAccountActions'
import { getFetchError, getIsFetchingOrNotStarted } from '@/reducers/fetch'
import { selectFinancialAccounts } from '@/selectors/financeSelectors'
import { getCurrentUser } from '@/selectors/user.selectors'
import { useReselector } from '@/utils/sharedHooks'
import { useAppDispatch } from '@/utils/typeHelpers'
import { readSkipped, writeSkipped } from './liveOnboarding.helpers'

type LiveOnboardingEligibility = 'loading' | 'eligible' | 'ineligible'

export const useLiveOnboardingEligibility = (
  enabled: boolean
): { eligibility: LiveOnboardingEligibility; skip: () => void } => {
  const dispatch = useAppDispatch()
  const currentUser = useReselector(getCurrentUser)
  const accounts = useReselector(selectFinancialAccounts)
  const accountsLoading = useReselector(
    getIsFetchingOrNotStarted,
    FETCH_FINANCIAL_ACCOUNT_KEY
  )
  const accountsError = useReselector(
    getFetchError,
    FETCH_FINANCIAL_ACCOUNT_KEY
  )
  const [skippedThisSession, setSkippedThisSession] = useState(false)
  const [accountsLoadedOnce, setAccountsLoadedOnce] = useState(false)

  const userId = currentUser?.id
  const skipped =
    skippedThisSession || (userId !== undefined && readSkipped(userId))
  const active = enabled && !skipped

  useEffect(() => {
    if (active) dispatch(fetchFinancialAccountsIfNeeded())
  }, [active, dispatch])

  // Other features refetch accounts in the background; going back to loading
  // would unmount the booking page and drop an in-progress booking.
  useEffect(() => {
    if (active && !accountsLoading) setAccountsLoadedOnce(true)
  }, [active, accountsLoading])

  const skip = useCallback(() => {
    if (userId !== undefined) writeSkipped(userId)
    setSkippedThisSession(true)
  }, [userId])

  if (!active) return { eligibility: 'ineligible', skip }
  if (accountsLoading && !accountsLoadedOnce) {
    return { eligibility: 'loading', skip }
  }
  const hasNoAccounts = !accountsError && Object.keys(accounts).length === 0
  return { eligibility: hasNoAccounts ? 'eligible' : 'ineligible', skip }
}
